import React, { useRef, useState } from 'react';
import {
  Button,
  Col,
  Modal,
  notification,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';

import { APP_TERMS } from '../../app-terms';
import EditClinicLocation from './EditClinicLocation';
import EditServiceCharges from './EditServiceCharges';
import { getClinicPaymentMethodMeta } from '../../utils/clinic';
import { useServicesList } from '../../hooks/services/useServicesList';

const { Text } = Typography;

const ClinicLocationsForm = (props) => {
  const { value: listData = [], onChange } = props;
  const formRef = useRef(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [editRecord, setEditRecord] = useState();
  const [ratesEditRecord, setRatesEditRecord] = useState();
  const [api, contextHolder] = notification.useNotification();
  const { data: servicesList, loading: loadingServiceList, getServicesList } = useServicesList();

  const handleCancel = () => {
    formRef?.current?.reset?.();
    setEditRecord(undefined);
  };
  const handleServiceChargesCancel = () => {
    setRatesEditRecord(undefined);
  };
  const handleCancelRates = () => {
    formRef?.current?.reset?.();
    setRatesEditRecord(undefined);
  };

  const editClinicLocation = (record) => {
    setEditRecord(record);
  };

  const addChangeClinicLocationRates = (record) => {
    setRatesEditRecord(record);
    // Fetch services list 
    getServicesList(record?.clinicLocationId);
  }

  const columns = [
    {
      title: 'Location Title',
      dataIndex: 'clinicLocationTitle',
      width: 200,
      render: (address) => (
        <Tooltip placement="topLeft" title={address}>
          {address}
        </Tooltip>
      ),
    },
    {
      title: 'Admin Email',
      dataIndex: 'adminEmail',
      width: 200,
      render: (adminEmail) => (
        <Tooltip placement="topLeft" title={adminEmail}>
          {adminEmail}
        </Tooltip>
      ),
    },
    {
      title: 'Account Email',
      dataIndex: 'accountEmail',
      width: 200,
      render: (accountEmail) => (
        <Tooltip placement="topLeft" title={accountEmail}>
          {accountEmail}
        </Tooltip>
      ),
    },
    {
      title: 'Doctor Email',
      dataIndex: 'doctorEmail',
      width: 200,
      render: (doctorEmail) => (
        <Tooltip placement="topLeft" title={doctorEmail}>
          {doctorEmail}
        </Tooltip>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      width: 250,
      render: (address) => (
        <Tooltip placement="topLeft" title={address}>
          {address}
        </Tooltip>
      ),
    },
    { title: 'Mobile', width: 150, dataIndex: 'contactNumber' },
    {
      title: 'Status',
      width: 120,
      dataIndex: 'status',
      render: (status, _, index) => {
        let color = status ? 'green' : 'geekblue';
        return (
          <Tag color={color} key={index}>
            {status ? 'Active' : 'In Active'}
          </Tag>
        );
      },
    },
    {
      title: 'Payment Method',
      width: 180,
      dataIndex: 'paymentMethod',
      render: (paymentMethodValue, _, index) => {
        let { color, text } = getClinicPaymentMethodMeta(paymentMethodValue);
        return (
          <Tag color={color} key={index}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: '',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="primary" onClick={() => editClinicLocation(record)}>
            Edit
          </Button>
          <Button type="primary" onClick={() => addChangeClinicLocationRates(record)}>
            Add/Change Rates
          </Button>
        </Space>
      ),
    },
  ];

  const handleSuccess = (data) => {
    const { createdInvoices = [], ...rest } = data;
    const index = listData?.findIndex(
      (item) => item?.clinicLocationId === rest?.clinicLocationId
    );
    const newData = [...listData];
    if (index > -1) {
      newData[index] = {
        ...listData[index],
        status: rest?.status,
        paymentMethod: rest?.paymentMethod,
        adminEmail: rest?.adminEmail ?? listData[index]?.adminEmail,
        accountEmail: rest?.accountEmail ?? listData[index]?.accountEmail,
        doctorEmail: rest?.doctorEmail ?? listData[index]?.doctorEmail,
      };
      onChange(newData);
      let message = 'Clinic location updated successfully!';
      if (
        editRecord?.status !== rest?.status &&
        editRecord?.paymentMethod !== rest?.paymentMethod
      ) {
        message = 'Clinic location details updated successfully!';
      } else if (editRecord?.paymentMethod !== rest?.paymentMethod) {
        message = 'Clinic location payment method updated successfully!';
      } else if (editRecord?.status !== rest?.status) {
        message = rest?.status
          ? 'Clinic location activated successfully!'
          : 'Clinic location deactivated successfully!';
      }
      api.success({
        message,
        description: createdInvoices?.length ? (
          <Text>
            `New invoices created are <b>${createdInvoices.join(',')}</b>`
          </Text>
        ) : (
          ''
        ),
      });
    }
    handleCancel();
  };
  const handleServiceChargesSuccess = () => {
    setRatesEditRecord(undefined);
  }

  const handleError = () => {
    api.error({ message: 'Failed to update clinic location!' });
  };

  return (
    <>
      {contextHolder}
      <Row gutter={16} className="mt-3">
        <Col>
          <h4 className="ms-2">{`${APP_TERMS.generic_clinic} ${APP_TERMS.generic_location_plural}`}</h4>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <div className="table-responsive doctor-list">
            <Table
              bordered
              columns={columns}
              scroll={{ x: 'max-content' }}
              tableLayout="fixed"
              dataSource={listData}
              pagination={{
                current: currentPageIndex,
                onChange: (page) => {
                  setCurrentPageIndex(page);
                },
              }}
              rowKey={(record) => {
                return record?.clinicLocationId;
              }}
            />
          </div>
        </Col>
      </Row>
      <Modal
        title={'Edit Clinic Location'}
        open={!!editRecord}
        onCancel={handleCancel}
        maskClosable={false}
        footer={null}
      >
        <EditClinicLocation
          formValues={editRecord ?? {}}
          onSuccess={handleSuccess}
          onError={handleError}
          ref={formRef}
        />
      </Modal>
      <Modal
        title={'Edit Clinic Location Service Charges'}
        open={!!ratesEditRecord}
        onCancel={handleCancelRates}
        loading={loadingServiceList}
        maskClosable={false}
        footer={null}
      >
        <EditServiceCharges
          clinicLocationId={ratesEditRecord?.clinicLocationId}
          servicesList={servicesList || []}
          onSuccess={handleServiceChargesSuccess}
          onCancel={handleServiceChargesCancel}
        />
        
      </Modal>
    </>
  );
};

ClinicLocationsForm.propTypes = {
  form: PropTypes.any,
  listData: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  clinicStatus: PropTypes.string,
};
export default ClinicLocationsForm;
